import React from 'react';
import { useTranslation } from 'react-i18next';
import { RapidFirePrompt } from '../../../types/lesson';
import { useLessonPersistedState } from '../useLessonPersistedState';
import TranslateToggle from './TranslateToggle';

interface Props {
    lessonId?: number;
    items: RapidFirePrompt[];
    targetVocabulary: string[];
    sourceLanguage?: string;
    targetLanguage?: string;
}

const RapidFireSection: React.FC<Props> = ({ lessonId, items, targetVocabulary, sourceLanguage, targetLanguage }) => {
    const { t } = useTranslation('lesson');
    const [index, setIndex] = useLessonPersistedState(lessonId, 'rapidfire.index', 0);
    const [showTranslation, setShowTranslation] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'rapidfire.showTranslation', {});
    const [fallback, setFallback] = useLessonPersistedState<Record<number, string>>(lessonId, 'rapidfire.fallbackTranslation', {});

    return (
        <div className="lesson-panel">
            <div>
                <div className="lesson-panel-kicker">{t('sections.rapid.kicker', 'Speak')}</div>
                <h2>{t('sections.rapid.title', 'Use It or Lose It')}</h2>
                <p className="lesson-panel-desc">{t('sections.rapid.desc', 'Use at least 3 target expressions from today.')}</p>
            </div>
            <div className="lesson-rapid-prompt lesson-word-target">
                {items[index]?.prompt}
                <TranslateToggle
                    text={items[index]?.prompt ?? ''}
                    native={items[index]?.prompt_native}
                    fallback={fallback[index]}
                    onFallbackFetched={(text) => setFallback((prev) => ({ ...prev, [index]: text }))}
                    sourceLanguage={sourceLanguage}
                    targetLanguage={targetLanguage}
                    shown={!!showTranslation[index]}
                    onToggle={() => setShowTranslation((prev) => ({ ...prev, [index]: !prev[index] }))}
                />
            </div>
            <div className="lesson-rapid-nav">
                <button className="lesson-btn ghost" disabled={index === 0} onClick={() => setIndex((i) => i - 1)}>
                    {t('sections.rapid.previous', '← Previous')}
                </button>
                <button className="lesson-btn" disabled={index === items.length - 1} onClick={() => setIndex((i) => Math.min(items.length - 1, i + 1))}>
                    {t('sections.rapid.next', 'Next prompt →')}
                </button>
            </div>
            <div className="lesson-rapid-dots">
                {items.map((_, i) => (
                    <span key={i} className={i === index ? 'active' : ''} />
                ))}
            </div>
            {targetVocabulary.length > 0 && (
                <div className="lesson-vocab-recap lesson-word-target">
                    {targetVocabulary.slice(0, 12).map((v, i) => (
                        <span key={i} className="chip">{v}</span>
                    ))}
                </div>
            )}
        </div>
    );
};

export default RapidFireSection;
